/*
 * PROD-9502: tie a student send back to the AI suggestion it came from, so the
 * server can write the #aimsg-salestask note. Lead tasks are never tagged: their
 * fixed templates are not suggestions and there is no student row to note.
 */

export const RECORD_AI_MESSAGE_URL = '/v2/sales_task_list/record_ai_message';
export const AI_NOTE_CHANNELS = ['chat', 'whatsapp'] as const;

export type AiNoteChannel = (typeof AI_NOTE_CHANNELS)[number];

export interface SuggestionSelection {
  version: number;
  option_index: number;
}

// goserver sendSMSRequest.
export interface SmsPayload {
  task_uuid: string;
  message: string;
  suggestion_version?: number;
  option_index?: number;
}

// goserver recordAIMessageRequest.
export interface AiNotePayload {
  task_uuid: string;
  suggestion_version: number;
  option_index: number;
  channel: AiNoteChannel;
  sent_text: string;
}

interface RecordAiMessageResponse {
  data?: { recorded?: boolean; data?: { recorded?: boolean } };
}

export type PostFn = (url: string, payload: AiNotePayload) => Promise<RecordAiMessageResponse>;

const isIndex = (n: unknown): n is number => Number.isInteger(n) && (n as number) >= 0;

// Playbook versions start at 1; the server rejects 0.
const isVersion = (n: unknown): n is number => Number.isInteger(n) && (n as number) > 0;

export const make_selection = (
  version: unknown,
  optionIndex: unknown
): SuggestionSelection | null =>
  isVersion(version) && isIndex(optionIndex) ? { version, option_index: optionIndex } : null;

const validSelection = (s: SuggestionSelection | null | undefined): s is SuggestionSelection =>
  !!s && isVersion(s.version) && isIndex(s.option_index);

// An emptied box no longer holds the suggestion; an edited one still does.
export const selection_after_edit = (
  selection: SuggestionSelection | null,
  text: unknown
): SuggestionSelection | null => (typeof text === 'string' && text.trim() ? selection : null);

export const sms_payload = (
  taskUuid: string,
  message: string,
  selection: SuggestionSelection | null | undefined,
  isLead = false
): SmsPayload => {
  const payload: SmsPayload = { task_uuid: taskUuid, message };
  if (!isLead && validSelection(selection)) {
    payload.suggestion_version = selection.version;
    payload.option_index = selection.option_index;
  }
  return payload;
};

const isChannel = (channel: unknown): channel is AiNoteChannel =>
  (AI_NOTE_CHANNELS as readonly unknown[]).includes(channel);

export const ai_note_payload = (
  taskUuid: string,
  selection: SuggestionSelection | null | undefined,
  channel: unknown,
  sentText: unknown,
  isLead = false
): AiNotePayload | null => {
  if (isLead || !taskUuid || !validSelection(selection)) return null;
  if (!isChannel(channel)) return null;
  if (typeof sentText !== 'string' || !sentText.trim()) return null;
  return {
    task_uuid: taskUuid,
    suggestion_version: selection.version,
    option_index: selection.option_index,
    channel,
    sent_text: sentText
  };
};

// Fire-and-forget: a failure must never reach the rep or block the send.
export const record_ai_message = (
  post: PostFn,
  payload: AiNotePayload | null
): Promise<boolean> => {
  if (!payload) return Promise.resolve(false);
  return Promise.resolve()
    .then(() => post(RECORD_AI_MESSAGE_URL, payload))
    .then((res) => !!(res?.data?.recorded ?? res?.data?.data?.recorded))
    .catch((e) => {
      // eslint-disable-next-line no-console
      console.error('record_ai_message failed', e);
      return false;
    });
};
